import { type LinkedProduct, LinksApi, PRODUCT_NAMES } from "@rhema/client";
import { Button, EmptyState, Logo } from "@rhema/ui";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { linkCallbackUrl } from "../platform/native";
import { useSession } from "../session/session";
import { hasPendingLink, takePendingLink } from "./pending";
import { useLinkOverview } from "./useLinks";

export type LinkedState = { linked?: string };

/**
 * Where SBI sends the browser after Google, Apple or Microsoft proves the other account. It
 * finishes the link this tab started, then goes back to Connected apps.
 */
export function LinkCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { links, reload } = useLinkOverview();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("link_request") ?? "";
    const product = token ? takePendingLink(token) : null;
    if (params.get("status") !== "verified") {
      setError(
        "The other app did not confirm your account, so nothing was linked. Try again from Connected apps.",
      );
      return;
    }
    if (!product) {
      setError("Rhema could not finish linking in this tab. Start again from Connected apps.");
      return;
    }
    links.api
      .confirm(product, token)
      .then(async () => {
        await reload();
        navigate("/you#connected-apps", { replace: true, state: { linked: product } satisfies LinkedState });
      })
      .catch((err: Error) => setError(`Linking ${PRODUCT_NAMES[product]} did not finish. ${err.message}`));
  }, [params, links, reload, navigate]);

  return (
    <EmptyState
      title={error ? "Not linked" : "Linking"}
      className="flex-1"
      action={
        error ? (
          <Button onClick={() => navigate("/you#connected-apps", { replace: true })}>
            Back to Connected apps
          </Button>
        ) : undefined
      }
    >
      {error ? <span role="alert">{error}</span> : "Finishing the link to your other app."}
    </EmptyState>
  );
}

/**
 * The page SBI returns every social link to. A link started in the desktop or mobile app arrives
 * here in the system browser, which has no Rhema session, so this asks the server whether the
 * token belongs to an app before the session gate runs. If it does, the browser goes back to the
 * app on a rhema:// link and the app confirms with its own session. Otherwise the web app finishes
 * the link in this tab.
 */
export function LinkCallbackGate() {
  const [params] = useSearchParams();
  const { api, redirect: open } = useSession();
  const token = params.get("link_request") ?? "";
  const [handoff, setHandoff] = useState<LinkedProduct | "web" | "checking">(() =>
    !token || hasPendingLink(token) ? "web" : "checking",
  );

  useEffect(() => {
    if (handoff !== "checking") return;
    let live = true;
    new LinksApi(api)
      .handoff(token)
      .catch(() => null)
      .then((product) => live && setHandoff(product ?? "web"));
    return () => {
      live = false;
    };
  }, [api, token, handoff]);

  if (handoff === "web") return <Outlet />;
  if (handoff === "checking") {
    return (
      <HandoffPage title="Linking">
        <p role="status" className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
          Finishing the link to your other app.
        </p>
      </HandoffPage>
    );
  }
  return (
    <NativeHandoff product={handoff} url={linkCallbackUrl(token, params.get("status") ?? "")} open={open} />
  );
}

function HandoffPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-linen px-6 text-center">
      <Logo size={72} variant="tile" />
      <h1 className="m-0 font-serif text-[26px] font-medium tracking-[-0.01em]">{title}</h1>
      {children}
    </main>
  );
}

function NativeHandoff({
  product,
  url,
  open,
}: {
  product: LinkedProduct;
  url: string;
  open: (url: string) => void;
}) {
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    opened.current = true;
    open(url);
  }, [open, url]);

  return (
    <HandoffPage title="Back to Rhema">
      <p className="m-0 max-w-sm text-[15px] leading-normal text-ink-secondary">
        Rhema is opening so the app can finish linking {PRODUCT_NAMES[product]}. If it does not open, use the
        button below. You can close this tab afterwards.
      </p>
      <Button onClick={() => open(url)}>Open Rhema</Button>
    </HandoffPage>
  );
}
